/**
 * UploadStep - File upload step for Bank Entry workflow
 */

import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useBankEntryStore } from '../../stores/bankEntryStore';
import { cn } from '../../lib/utils';
import { Upload, FileSpreadsheet, ArrowRight, X } from 'lucide-react';
import RulesDialog from './RulesDialog';

export const UploadStep: React.FC = () => {
  const { setFile, setCurrentStep, setError, file } = useBankEntryStore();
  const { toast } = useToast();
  const [showRules, setShowRules] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
      ];

      if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
        setError('Please upload an Excel (.xlsx, .xls) or CSV file');
        toast({
          title: 'Invalid file type',
          description: 'Please upload an Excel or CSV file',
          variant: 'destructive',
        });
        return;
      }

      setFile(file);
      setError(null);
    }
  }, [setFile, setError, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleContinue = () => {
    console.log('[BankEntry] UploadStep handleContinue - file:', file?.name);
    if (file) {
      console.log('[BankEntry] Setting currentStep to "select"');
      setCurrentStep('select');
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Bank Statement
            </CardTitle>
            <CardDescription>
              Upload your bank statement in Excel or CSV format
            </CardDescription>
          </div>
          <Button variant="outline" onClick={() => setShowRules(true)}>
            Custom Rules
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors",
            isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50",
            file && "border-green-500 bg-green-50"
          )}
        >
          <input {...getInputProps()} />
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <FileSpreadsheet className="w-12 h-12 text-green-500" />
              <p className="font-medium">{file.name}</p>
              <p className="text-sm text-muted-foreground">
                {(file.size / 1024).toFixed(1)} KB
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                }}
              >
                <X className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-12 h-12 text-muted-foreground" />
              <p className="font-medium">
                {isDragActive ? 'Drop the file here' : 'Drag & drop your bank statement'}
              </p>
              <p className="text-sm text-muted-foreground">
                or click to browse (Excel, CSV)
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <Button onClick={handleContinue} disabled={!file}>
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
      <RulesDialog open={showRules} onOpenChange={setShowRules} />
    </Card>
  );
};

export default UploadStep;
